import React, { useRef, useState } from 'react';

import Icon from 'components/DesignSystem/Icon';
import Segmented from 'components/DesignSystem/Segmented';
import SelectorChip from 'components/DesignSystem/SelectorChip';
import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardChips from 'components/Meteorology/Shared/QueryCard/Chips';
import QueryCardChip from 'components/Meteorology/Shared/QueryCard/Chip';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import TFDVChart from 'components/Meteorology/Agro/TrackingForecast/DetailedView/Chart';
import useDetailedViewMobile from 'hooks/Meteorology/Agro/TrackingForecast/DetailedView/Mobile/useDetailedViewMobile';
import useChartData from 'hooks/Meteorology/Agro/TrackingForecast/DetailedView/useChartData';
import useHeatmapAwareDownload from 'hooks/Meteorology/TrackingForecast/useHeatmapAwareDownload';
import { SUBPAGE_NAMES } from 'types/Meteorology/Agro/TrackingForecast/DetailedView';
import { cssVar } from 'constants/theme';

import ControlPanelSheet from './ControlPanelSheet';
import FullscreenChart from './FullscreenChart';

import './index.scss';

export default function DetailedViewMobile() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const {
    subpage,
    subpageLabel,
    subpageButtonProps,
    chartMode,
    setChartMode,
    chips,
  } = useDetailedViewMobile();
  const dvImages = useRef<HTMLDivElement>(null);
  // #2009 item 8: heatmap na tela → PDF com a tabela inteira; gráfico de linhas → imagem.
  const { data: chartOption } = useChartData();
  const { download, label: downloadLabel } = useHeatmapAwareDownload(dvImages, chartOption);
  const subpageName = SUBPAGE_NAMES[subpage] ?? SUBPAGE_NAMES.locations;
  const filename = `agro_acompanhamento_previsao_${subpageName.split(' ').join('_').toLowerCase()}`;

  return (
    <div className="agro-tf-dv-mobile">
      <div className="agro-tf-dv-mobile-pills">
        {subpageButtonProps.map((buttonProps) => (
          <SelectorChip
            key={buttonProps.text}
            selected={buttonProps.active}
            onClick={buttonProps.onClick}
          >
            {buttonProps.text}
          </SelectorChip>
        ))}
      </div>

      <QueryCard>
        <QueryCardHeader label="Consulta atual" title={subpageLabel}>
          {chips.length > 0 && (
            <QueryCardChips>
              {chips.map((chip) => <QueryCardChip key={chip}>{chip}</QueryCardChip>)}
            </QueryCardChips>
          )}
        </QueryCardHeader>
        <QueryCardTrigger onClick={() => setSheetOpen(true)} />
      </QueryCard>

      <div className="agro-tf-dv-mobile-chart-card">
        <div className="agro-tf-dv-mobile-chart-card-header">
          <Segmented
            className="agro-tf-dv-mobile-chart-mode-switch"
            size="sm"
            fill={false}
            ariaLabel="Tipo de gráfico"
            options={[
              { value: 'line', icon: 'line_axis', ariaLabel: 'Gráfico de linhas' },
              { value: 'heatmap', icon: 'table_rows', ariaLabel: 'Heatmap' },
            ]}
            value={chartMode}
            onChange={setChartMode}
          />
          <div className="agro-tf-dv-mobile-chart-card-actions">
            <button
              type="button"
              aria-label={downloadLabel}
              className="agro-tf-dv-mobile-chart-card-action"
              onClick={download(filename)}
            >
              <Icon iconType="download" color={cssVar('text-strong')} size="small" />
            </button>
            <button
              type="button"
              aria-label="Tela cheia"
              className="agro-tf-dv-mobile-chart-card-action"
              onClick={() => setFullscreenOpen(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVar('text-strong')} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
              </svg>
            </button>
          </div>
        </div>
        <div ref={dvImages}>
          <TFDVChart />
        </div>
      </div>

      <ControlPanelSheet open={sheetOpen} dismiss={() => setSheetOpen(false)} />
      <FullscreenChart open={fullscreenOpen} dismiss={() => setFullscreenOpen(false)} />
    </div>
  );
}
